import { ButtonBase } from '@mui/material';
import { useDialog } from 'contexts/dialog';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormLabel,
    Icon,
    IconButton,
    MenuItem,
    Paper,
    ResponsiveDialog,
    Select,
    Stack,
    styled,
    Text,
    TextArea,
    TextInput,
} from 'shell/components';
import { getIconKind, getResourceName, getResourceType } from 'shell/components/ResourceChip';
import { CONTENT_BANK_RESOURCES_MB_LIMIT } from 'utils/constants';
import { bytesToMB, downloadFileFromURL, getS3FileURL, getWordCount } from 'utils/helper';

const RESOURCE_DESCRIPTION_WORD_COUNT_LIMIT = 200;

const EditContentBankResourcesDialog = memo(({ readOnly = false, open, resources, onClose, onSave }) => {
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState(resources);
    const [errors, setErrors] = useState({});
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const validate = useCallback(
        (resource) => {
            const getError = (resource, key) => {
                return {
                    resource_type: !Boolean(resource.resource_type)
                        ? t('error.invalid-resource-type', 'Please select a resource type')
                        : '',
                    title: resource.resource_type === 'other' && !resource.title,
                    description: getWordCount(resource.description) > RESOURCE_DESCRIPTION_WORD_COUNT_LIMIT,
                }[key];
            };

            const resourceTypeError = getError(resource, 'resource_type');
            const titleError = getError(resource, 'title');
            const descriptionError = getError(resource, 'description');

            setErrors((current) => ({
                ...current,
                [resource.id]: {
                    resource_type: resourceTypeError,
                    title: titleError,
                    description: descriptionError,
                },
            }));

            return !Boolean(resourceTypeError) && !Boolean(titleError) && !Boolean(descriptionError);
        },
        [t]
    );

    const validateAll = () => {
        return form
            .filter(({ active }) => active)
            .map((resource) => {
                return validate(resource);
            })
            .reduce((acc, cur) => acc && cur, true);
    };

    const handleClose = () => {
        if (readOnly) return onClose();
        setDialog({
            open: true,
            message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'common:dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                onSave(resources.filter(({ resource_type }) => Boolean(resource_type)));
                onClose();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickDelete = useCallback(
        (targetId) => {
            setDialog({
                open: true,
                message: t('dialog.delete-file.title', 'Delete File?'),
                description: t(
                    'dialog.delete-file.description',
                    'Are you sure you want to delete this file? This is permanent and cannot be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => {
                        return current.map((resource) => {
                            if (resource.id !== targetId) return resource;
                            return {
                                ...resource,
                                active: false,
                                modified_on: new Date().getTime(),
                            };
                        });
                    });
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        },
        [closeDialog, setDialog, t]
    );

    const handleChange = useCallback(
        (resourceId, key, value) => {
            setForm((current) => {
                return current.map((resource) => {
                    if (resource.id !== resourceId) return resource;
                    const newResource = {
                        ...resource,
                        [key]: value,
                    };
                    if (key === 'resource_type' && value !== 'other') {
                        delete newResource.title;
                    }
                    if (hasClickedSubmit) validate(newResource);
                    return newResource;
                });
            });
        },
        [hasClickedSubmit, validate]
    );

    const handleClickSave = () => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            onSave(form);
            onClose();
        }
    };

    useEffect(() => {
        if (open) {
            setForm(
                resources.sort((a, b) => {
                    if (a.created_on === b.created_on) return 0;
                    return b.created_on - a.created_on;
                })
            );
        } else {
            setForm([]);
            setErrors({});
            setHasClickedSubmit(false);
        }
    }, [open, resources]);

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={3}>
                        <Text component={'h1'} variant={'h6'} weight={'medium'}>
                            {t('content-bank.title', 'Content Bank')}
                        </Text>
                        <Text color={'light'}>
                            [
                            {t('content-bank.mb-used', '{{mbUsed}} MB used', {
                                mbUsed: `${bytesToMB(
                                    form.filter(({ active }) => active).reduce((acc, cur) => acc + cur.data.size, 0)
                                )}/${CONTENT_BANK_RESOURCES_MB_LIMIT}`,
                            })}
                            ]
                        </Text>
                    </Stack>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={readOnly ? onClose : handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <DialogContent>
                {form.filter(({ active }) => active).length ? (
                    <CustomPaper variant={'outlined'}>
                        {form
                            .filter(({ active }) => active)
                            .map((resource) => {
                                return (
                                    <MemoizedResource
                                        key={resource.id}
                                        resource={resource}
                                        errors={errors}
                                        readOnly={readOnly}
                                        handleChange={handleChange}
                                        handleClickDelete={handleClickDelete}
                                    />
                                );
                            })}
                    </CustomPaper>
                ) : (
                    <Text align={'center'} color={'light'} component={'i'}>
                        {t('content-bank.empty', 'Your Content Bank is empty.')}
                    </Text>
                )}
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button onClick={readOnly ? onClose : handleClickSave}>
                    {readOnly
                        ? t('common:controls.close', 'Close')
                        : t('common:controls.save', t('common:controls.save', 'Save'))}
                </Button>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const MemoizedResource = memo(({ errors, readOnly, resource, handleChange, handleClickDelete }) => {
    const { t } = useTranslation('assignment');

    const RESOURCE_TYPES = [
        { 'course-syllabus': t('content-bank.course-syllabus', 'Course Syllabus') },
        { 'exemplar-paper': t('content-bank.exemplar-paper', 'Exemplar Paper (High-Scoring)') },
        { 'grading-guidelines': t('content-bank.grading-guidelines', 'Grading Guidelines') },
        { 'lecture-slides': t('content-bank.lecture-slides', 'Lecture Slides') },
        { 'reading-materials': t('content-bank.reading-materials', 'Reading Materials') },
        { 'answer-key': t('content-bank.answer-key', 'Answer Key') },
        { other: t('content-bank.other', 'Other') },
    ];

    const memoizedInputProps = useMemo(() => {
        return { readOnly };
    }, [readOnly]);

    const memoizedOnChange = useCallback(
        (e) => handleChange(resource.id, 'description', e.target.value),
        [handleChange, resource.id]
    );

    return (
        <ResourceStack key={resource.id} spacing={3}>
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <CustomButtonBase onClick={() => downloadFileFromURL(getS3FileURL(resource.data))}>
                    <Stack alignItems={'center'} direction={'row'} spacing={3}>
                        <CustomIcon kind={getIconKind('file', resource)} />
                        <Stack alignItems={'flex-start'}>
                            <Text align={'left'}>{getResourceName('file', resource)}</Text>
                            <Text color={'light'}>{getResourceType('file', resource, t)}</Text>
                        </Stack>
                    </Stack>
                </CustomButtonBase>
                {!readOnly && (
                    <IconButton
                        aria-label={`delete ${getResourceName('file', resource)}`}
                        kind={'delete'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => handleClickDelete(resource.id)}
                    />
                )}
            </Stack>
            <Stack spacing={1}>
                <FormLabel>
                    {t('content-bank.resource-type', 'Resource Type')}{' '}
                    <Text color={'error'} component={'span'}>
                        *
                    </Text>
                </FormLabel>
                <Select
                    error={Boolean(errors[resource.id]?.resource_type)}
                    inputProps={{
                        'aria-label': `select a resource type for ${resource.data.name}`,
                    }}
                    readOnly={readOnly}
                    required
                    value={resource.resource_type}
                    onChange={(e) => handleChange(resource.id, 'resource_type', e.target.value)}
                >
                    {RESOURCE_TYPES.map((item, index) => (
                        <MenuItem key={index} value={Object.keys(item)[0]}>
                            {Object.values(item)[0]}
                        </MenuItem>
                    ))}
                </Select>
                {Boolean(errors[resource.id]?.resource_type) && (
                    <Text color={'error'} variant={'p3'}>
                        {errors[resource.id].resource_type}
                    </Text>
                )}
            </Stack>
            {resource.resource_type === 'other' && (
                <TextInput
                    error={Boolean(errors[resource.id]?.title)}
                    label={t('content-bank.please-specify', 'Please Specify')}
                    InputProps={{ readOnly }}
                    required
                    value={resource.title || ''}
                    onChange={(e) => handleChange(resource.id, 'title', e.target.value)}
                />
            )}
            <TextArea
                aria-label={`description for ${resource.data.name}`}
                error={Boolean(errors[resource.id]?.description)}
                helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                    wordLimitStatus: `${getWordCount(resource.description)}/${RESOURCE_DESCRIPTION_WORD_COUNT_LIMIT}`,
                })}
                helperTextPosition={'end'}
                InputProps={memoizedInputProps}
                label={t('content-bank.description-label', 'How should the AI use this information?')}
                placeholder={t(
                    'content-bank.description-placeholder',
                    'Ex. Use this exemplar paper as a baseline for grading. Analyze its structure, content, and quality to set a standard for high-scoring work.'
                )}
                rows={6}
                value={resource.description}
                onChange={memoizedOnChange}
            />
        </ResourceStack>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            maxWidth: '700px',
            width: '100%',
            height: '100%',
        },
    };
});

const CustomPaper = styled(Paper)(() => {
    return {
        padding: 0,
    };
});

const ResourceStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(5),
        '&:nth-child(odd)': {
            background: theme.palette.ensightful.neutral[50],
        },
        '&:nth-child(even)': {
            background: theme.palette.ensightful.neutral[100],
        },
        '&:first-child': {
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
        },
        '&:last-child': {
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px',
        },
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        '&.Mui-focusVisible': {
            borderRadius: '6px',
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomIcon = styled(Icon)(() => {
    return {
        fontSize: '2rem',
    };
});

export default EditContentBankResourcesDialog;
